import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Badge,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tab,
  Tabs,
  Stack
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  PlayArrow as ActiveIcon,
  Pause as DelayedIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  GetApp as ExportIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { PAYMENT_TYPES } from '../../constants/statusCodes';
import PERMISSIONS from '../../config/permissionsList';
import { getTokenAuth } from '../../utils/authUtils';

/**
 * Interface d'administration des jobs automatisés
 * Permet de visualiser, gérer et surveiller tous les jobs automatisés du système
 */
const AutomatedJobsAdmin = () => {
  const { user } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [activeJobs, setActiveJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // States pour la pagination et les filtres
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalJobs, setTotalJobs] = useState(0);
  const [filters, setFilters] = useState({
    job_type: '',
    job_category: '',
    status: '',
    target_type: '',
    date_from: '',
    date_to: ''
  });

  // States pour les dialogues
  const [selectedJob, setSelectedJob] = useState(null);
  const [cancelDialog, setCancelDialog] = useState({ open: false, job: null, reason: '' });
  const [detailsDialog, setDetailsDialog] = useState({ open: false, job: null });

  /**
   * Vérification des permissions utilisateur
   */
  const hasPermission = (permission) => {
    if (user?.isAdmin) return true; // Les admins ont toutes les permissions
    return user?.permissions?.includes(permission) || false;
  };

  /**
   * Chargement des données au montage du composant
   */
  useEffect(() => {
    loadAllData();
  }, [page, rowsPerPage, filters]);

  /**
   * Chargement de toutes les données
   */
  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadJobs(),
        loadStatistics(),
        loadActiveJobs()
      ]);
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Chargement de la liste des jobs
   */
  const loadJobs = async () => {
    try {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const response = await fetch(`/api/v1/admin/automated-jobs?${params}`, {
        headers: {
          'Authorization': `Bearer ${getTokenAuth()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Erreur de chargement');

      const data = await response.json();
      setJobs(data.data.jobs);
      setTotalJobs(data.data.pagination.total_jobs);
    } catch (err) {
      console.error('Erreur chargement jobs:', err);
      throw err;
    }
  };

  /**
   * Chargement des statistiques
   */
  const loadStatistics = async () => {
    try {
      const response = await fetch('/api/v1/admin/automated-jobs/statistics', {
        headers: {
          'Authorization': `Bearer ${getTokenAuth()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Erreur de chargement des statistiques');

      const data = await response.json();
      setStatistics(data.data.statistics);
    } catch (err) {
      console.error('Erreur statistiques:', err);
      throw err;
    }
  };

  /**
   * Chargement des jobs actifs
   */
  const loadActiveJobs = async () => {
    try {
      const response = await fetch('/api/v1/admin/automated-jobs/active', {
        headers: {
          'Authorization': `Bearer ${getTokenAuth()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Erreur de chargement des jobs actifs');

      const data = await response.json();
      setActiveJobs(data.data.active_jobs);
    } catch (err) {
      console.error('Erreur jobs actifs:', err);
      throw err;
    }
  };

  /**
   * Annulation d'un job
   */
  const handleCancelJob = async () => {
    if (!cancelDialog.job || !cancelDialog.reason) return;

    try {
      const response = await fetch(`/api/v1/admin/automated-jobs/${cancelDialog.job.job_id}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getTokenAuth()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: cancelDialog.reason })
      });

      if (!response.ok) throw new Error('Erreur lors de l\'annulation');

      setCancelDialog({ open: false, job: null, reason: '' });
      loadAllData(); // Recharger les données
      
      // Afficher un message de succès
      setError(null);
    } catch (err) {
      setError(`Erreur lors de l'annulation: ${err.message}`);
    }
  };

  /**
   * Nettoyage des jobs expirés
   */
  const handleCleanupExpired = async () => {
    try {
      const response = await fetch('/api/v1/admin/automated-jobs/cleanup-expired', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getTokenAuth()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Erreur lors du nettoyage');

      const data = await response.json();
      alert(`${data.data.cancelled_jobs} jobs expirés ont été nettoyés`);
      loadAllData();
    } catch (err) {
      setError(`Erreur lors du nettoyage: ${err.message}`);
    }
  };

  /**
   * Obtient la couleur pour le statut du job
   */
  const getStatusColor = (status) => {
    const colors = {
      'scheduled': 'warning',
      'active': 'info',
      'completed': 'success',
      'failed': 'error',
      'cancelled': 'secondary',
      'delayed': 'warning'
    };
    return colors[status] || 'default';
  };

  /**
   * Obtient l'icône pour le statut du job
   */
  const getStatusIcon = (status) => {
    const icons = {
      'scheduled': <ScheduleIcon fontSize="small" />,
      'active': <ActiveIcon fontSize="small" />,
      'completed': <CompletedIcon fontSize="small" />,
      'failed': <FailedIcon fontSize="small" />,
      'cancelled': <CancelIcon fontSize="small" />,
      'delayed': <DelayedIcon fontSize="small" />
    };
    return icons[status] || <ScheduleIcon fontSize="small" />;
  };

  /**
   * Formatage des types de jobs
   */
  const getJobTypeLabel = (type) => {
    const labels = {
      [PAYMENT_TYPES.DEPOSIT_REFUND]: '💰 Remboursement Dépôt',
      'deposit_auto_capture': '🔒 Capture Auto Dépôt',
      'reservation_reminder': '📅 Rappel Réservation',
      'payment_retry': '💳 Retry Paiement',
      'cleanup_expired': '🧹 Nettoyage Expiré',
      'notification_batch': '📧 Notifications Lot',
      'report_generation': '📊 Génération Rapport'
    };
    return labels[type] || type;
  };

  /**
   * Rendu de la section des statistiques
   */
  const renderStatistics = () => {
    if (!statistics) return null;

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h4" color="primary">
                {statistics.total_jobs}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Jobs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <Badge badgeContent={statistics.active_jobs} color="info">
                <Typography variant="h4" color="info.main">
                  {statistics.active_jobs}
                </Typography>
              </Badge>
              <Typography variant="body2" color="textSecondary">
                Jobs Actifs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h4" color="success.main">
                {statistics.completed_jobs}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Terminés
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h4" color="error.main">
                {statistics.failed_jobs}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Échoués
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h4" color="success.main">
                {statistics.success_rate}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Taux Succès
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  /**
   * Rendu des filtres
   */
  const renderFilters = () => (
    <Accordion sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterIcon sx={{ mr: 1 }} />
          Filtres de recherche
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Type de Job</InputLabel>
              <Select
                value={filters.job_type}
                onChange={(e) => setFilters({ ...filters, job_type: e.target.value })}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value={PAYMENT_TYPES.DEPOSIT_REFUND}>Remboursement Dépôt</MenuItem>
                <MenuItem value="deposit_auto_capture">Capture Auto Dépôt</MenuItem>
                <MenuItem value="reservation_reminder">Rappel Réservation</MenuItem>
                <MenuItem value="payment_retry">Retry Paiement</MenuItem>
                <MenuItem value="cleanup_expired">Nettoyage Expiré</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="scheduled">Programmé</MenuItem>
                <MenuItem value="active">Actif</MenuItem>
                <MenuItem value="completed">Terminé</MenuItem>
                <MenuItem value="failed">Échoué</MenuItem>
                <MenuItem value="cancelled">Annulé</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Date début"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Date fin"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  /**
   * Rendu du tableau des jobs
   */
  const renderJobsTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Job ID</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Statut</TableCell>
            <TableCell>Cible</TableCell>
            <TableCell>Programmé</TableCell>
            <TableCell>Tentatives</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.map((job) => (
            <TableRow key={job.id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {job.job_id.substring(0, 12)}...
                </Typography>
              </TableCell>
              <TableCell>
                <Chip 
                  label={getJobTypeLabel(job.job_type)}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Chip
                  icon={getStatusIcon(job.status)}
                  label={job.status.toUpperCase()}
                  color={getStatusColor(job.status)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {job.target_type && (
                  <Typography variant="body2">
                    {job.target_type}: {job.target_id?.substring(0, 8)}...
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {format(new Date(job.scheduled_at), 'dd/MM/yy HH:mm')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatDistanceToNow(new Date(job.scheduled_at), { 
                    addSuffix: true, 
                    locale: fr 
                  })}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {job.attempt_count} / {job.max_attempts}
                </Typography>
                {job.attempt_count > 0 && (
                  <LinearProgress 
                    variant="determinate" 
                    value={(job.attempt_count / job.max_attempts) * 100}
                    sx={{ mt: 0.5 }}
                  />
                )}
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Voir détails">
                    <IconButton
                      size="small"
                      onClick={() => setDetailsDialog({ open: true, job })}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  {['scheduled', 'delayed'].includes(job.status) && (
                    <Tooltip title="Annuler">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setCancelDialog({ open: true, job, reason: '' })}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalJobs}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
        labelRowsPerPage="Lignes par page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </TableContainer>
  );

  /**
   * Rendu des jobs actifs
   */
  const renderActiveJobs = () => (
    <Grid container spacing={2}>
      {activeJobs.map((job) => (
        <Grid item xs={12} md={6} lg={4} key={job.id}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Chip
                  label={getJobTypeLabel(job.job_type)}
                  size="small"
                  color="primary"
                />
                <Chip
                  icon={getStatusIcon(job.status)}
                  label={job.status.toUpperCase()}
                  color={getStatusColor(job.status)}
                  size="small"
                />
              </Box>
              
              <Typography variant="h6" gutterBottom>
                Job {job.job_id.substring(0, 8)}...
              </Typography>
              
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Cible:</strong> {job.target_type} ({job.target_id?.substring(0, 12)}...)
              </Typography>
              
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Programmé:</strong> {format(new Date(job.scheduled_at), 'dd/MM/yy HH:mm')}
              </Typography>
              
              <Typography variant="body2" color="textSecondary">
                <strong>Tentatives:</strong> {job.attempt_count} / {job.max_attempts}
              </Typography>
              
              {job.started_at && (
                <Typography variant="caption" color="primary">
                  Démarré: {formatDistanceToNow(new Date(job.started_at), { 
                    addSuffix: true, 
                    locale: fr 
                  })}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
      {activeJobs.length === 0 && (
        <Grid item xs={12}>
          <Alert severity="info">
            Aucun job actif en cours d'exécution
          </Alert>
        </Grid>
      )}
    </Grid>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Chargement des données...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
          🤖 Gestion des Jobs Automatisés
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAllData}
            disabled={loading}
          >
            Actualiser
          </Button>
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={handleCleanupExpired}
            color="warning"
          >
            Nettoyer Expirés
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistiques */}
      {renderStatistics()}

      {/* Onglets */}
      <Tabs value={currentTab} onChange={(e, newTab) => setCurrentTab(newTab)} sx={{ mb: 2 }}>
        <Tab label="Tous les Jobs" />
        <Tab 
          label={
            <Badge badgeContent={activeJobs.length} color="info">
              Jobs Actifs
            </Badge>
          } 
        />
        <Tab label="Statistiques" />
      </Tabs>

      {/* Contenu des onglets */}
      {currentTab === 0 && (
        <Box>
          {renderFilters()}
          {renderJobsTable()}
        </Box>
      )}

      {currentTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Jobs Actifs et Programmés
          </Typography>
          {renderActiveJobs()}
        </Box>
      )}

      {currentTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Statistiques Détaillées
          </Typography>
          {statistics && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Répartition par Statut
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography>Terminés: {statistics.completed_jobs}</Typography>
                      <Typography>Échoués: {statistics.failed_jobs}</Typography>
                      <Typography>Actifs: {statistics.active_jobs}</Typography>
                      <Typography>Total: {statistics.total_jobs}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Performance
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h4" color="success.main">
                        {statistics.success_rate}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Taux de succès global
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* Dialog de détails */}
      <Dialog 
        open={detailsDialog.open} 
        onClose={() => setDetailsDialog({ open: false, job: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Détails du Job {detailsDialog.job?.job_id}
        </DialogTitle>
        <DialogContent>
          {detailsDialog.job && (
            <Box sx={{ mt: 2 }}>
              <Typography><strong>Type:</strong> {getJobTypeLabel(detailsDialog.job.job_type)}</Typography>
              <Typography><strong>Statut:</strong> {detailsDialog.job.status}</Typography>
              <Typography><strong>Cible:</strong> {detailsDialog.job.target_type} - {detailsDialog.job.target_id}</Typography>
              <Typography><strong>Programmé:</strong> {format(new Date(detailsDialog.job.scheduled_at), 'dd/MM/yyyy HH:mm:ss')}</Typography>
              <Typography><strong>Tentatives:</strong> {detailsDialog.job.attempt_count} / {detailsDialog.job.max_attempts}</Typography>
              <Typography><strong>Créé par:</strong> {detailsDialog.job.created_by}</Typography>
              
              {detailsDialog.job.job_data && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6">Données du Job:</Typography>
                  <pre style={{ fontSize: '12px', backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                    {JSON.stringify(detailsDialog.job.job_data, null, 2)}
                  </pre>
                </Box>
              )}

              {detailsDialog.job.error_details && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" color="error">Détails d'Erreur:</Typography>
                  <Alert severity="error">
                    {detailsDialog.job.error_details.message}
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog({ open: false, job: null })}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'annulation */}
      <Dialog 
        open={cancelDialog.open} 
        onClose={() => setCancelDialog({ open: false, job: null, reason: '' })}
      >
        <DialogTitle>
          Annuler le Job {cancelDialog.job?.job_id?.substring(0, 12)}...
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Raison de l'annulation"
            value={cancelDialog.reason}
            onChange={(e) => setCancelDialog({ ...cancelDialog, reason: e.target.value })}
            sx={{ mt: 2 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCancelDialog({ open: false, job: null, reason: '' })}
          >
            Annuler
          </Button>
          <Button
            onClick={handleCancelJob}
            color="error"
            variant="contained"
            disabled={!cancelDialog.reason.trim()}
          >
            Confirmer l'annulation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AutomatedJobsAdmin;
